import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'

  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import TextRenderer from '@/components/editor/TextRenderer.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import Dropdown from '@/components/common/Dropdown.vue'
  import VideoOrImageRenderer from '@/components/course/VideoOrImageRenderer.vue'
  import CollapsibleWidget from '@/components/common/CollapsibleWidget.vue'
  import DurationInput from '@/components/common/DurationInput.vue'

  import { useRoute } from 'vue-router/composables'
  import useIcons from '@/composables/useIcons'
  import useEditor from '@/composables/useEditor'
  import useCourse from '@/composables/useCourse'
  import useI18n from '@/composables/useI18n'

  import { EditorModule } from '@/store/modules/editor'
  import { CourseModule } from '@/store/modules/course'

  import CourseApi from '@/services/api/CourseApi'

  import { v4 as uuidv4 } from 'uuid'
  import eventBus from '@/main'

  import type { PropType } from 'vue'
  import type {
    CourseModule as CourseModuleState,
    LessonQuestion,
    EditorStatePayload,
    LessonMap,
    EditorQuestion,
    EditorAnswer,
  } from '@/services/interfaces/Course'
  import type { Nullable } from '@/services/interfaces/Content'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'QuizQuestions',
  props: {
    originalQuestions: {
      type: Array as PropType<LessonQuestion[]>,
      required: true,
    },
    lessonQuestions: {
      type: Array as PropType<EditorQuestion[]>,
      required: true,
    },
    addLessonQuestion: {
      type: Function as PropType<(question: EditorQuestion) => void>,
      required: true,
    },
    removeLessonQuestion: {
      type: Function as PropType<(index: number) => EditorQuestion[]>,
      required: true,
    },
    canDrag: Boolean,
  },
  setup(__props) {

const props = __props;

  

  const route = useRoute()
  const { translateString } = useI18n()
  const { checkboxSquareChecked, checkboxSquare } = useIcons()
  const { hasLocalChanges } = useEditor(route)
  const { currentCourseId } = useCourse()

  const questionStates = reactive({} as { [key: string]: boolean })
  const delayOpen = reactive({} as { [key: string]: boolean })

  const transitionName = ref('')

  const questionAnswers = computed(() => {
    return (answers: EditorAnswer[]) => {
      return answers.filter((a) => a._destroy !== true)
    }
  })

  const originalQuestion = computed(
    () => (questionId?: string) => props.originalQuestions.find((q) => q.id === questionId),
  )

  const questionHeaderTitle = computed(() => {
    return (question: EditorQuestion | LessonQuestion) => {
      return (
        question.title ||
        originalQuestion.value(question.id || '')?.title ||
        translateString('editor.questions.newQuestion')
      )
    }
  })

  const addNewQuestion = () => {
    transitionName.value = 'fadeInUp'
    const editorId = 'editor-question-' + uuidv4()
    props.addLessonQuestion({
      editorId,
      title: '',
      questionType: '',
      answers: [],
      comment: '',
      imageUrl: null,
      imageData: '',
      points: 1,
    })
    eventBus.$set(delayOpen, editorId, false)
    eventBus.$set(questionStates, editorId, false)
  }
  const addNewAnswer = (index: number) => {
    transitionName.value = 'fadeInUp'
    const question = props.lessonQuestions[index]
    const editorAnswerId = 'editor-answer-' + uuidv4()
    question.answers.push({
      editorAnswerId,
      correct: false,
      details: '',
    })
  }

  const dropdownItems = [
    { selectable: true, type: 'multiple', title: 'Multiple Choice' },
    { selectable: true, type: 'single', title: 'Single Choice' },
    { selectable: true, type: 'number', title: 'Number' },
  ]

  const removeQuestion = (question: EditorQuestion, index: number) => {
    transitionName.value = 'next-slide'
    if (question.id) {
      eventBus.$set(question, '_destroy', true)
    } else {
      props.removeLessonQuestion(index)
    }
  }
  const getAnswer = (questionIndex: number, answerId: string) => {
    const question = props.lessonQuestions[questionIndex]
    return question.answers.find((a) => [answerId].includes(a.id || a.editorAnswerId!)) as EditorAnswer
  }

  const onQuestionTypeChange = (question: EditorQuestion, newType: 'number' | 'single' | 'multiple') => {
    question.questionType = newType
    if (question.answers.length > 1) {
      if (newType === 'single') {
        let foundFirstSelectedAnswer = false
        question.answers = question.answers.map((a) => {
          if (foundFirstSelectedAnswer) a.correct = false
          if (a.correct) foundFirstSelectedAnswer = true
          return a
        })
      } else if (newType === 'number') {
        const index = question.answers.findIndex((a) => a.correct)
        question.answers = question.answers.splice(index === -1 ? 0 : index, 1)
      }
    }
  }

  const onCorrectAnswer = (questionIndex: number, answerId: string) => {
    const question = props.lessonQuestions[questionIndex]
    const answer = question.answers.find((a) => [answerId].includes(a.id || a.editorAnswerId!)) as EditorAnswer
    const correctAnswers = question.answers.filter((a) => a.correct)

    if (
      ['number', 'multiple'].includes(question.questionType) ||
      (question.questionType === 'single' &&
        (correctAnswers.length === 0 || (correctAnswers.length === 1 && correctAnswers[0] === answer)))
    ) {
      answer.correct = !answer.correct
    }
  }
  const removeAnswer = (questionIndex: number, answer: EditorAnswer) => {
    transitionName.value = 'next-slide'
    const question = props.lessonQuestions[questionIndex]
    const answerIndex = question.answers.findIndex((a) => {
      return [answer.id || answer.editorAnswerId].includes(a.id || a.editorAnswerId)
    })
    if (answerIndex > -1 && !answer.id) question.answers.splice(answerIndex, 1)
    if (answer.id) eventBus.$set(answer, '_destroy', true)
  }

  const removeQuestionImage = (index: number) => {
    const question = props.lessonQuestions[index]
    question.imageUrl = null
  }
  const onImageUpload = (index: number, imageData: string) => {
    const question = props.lessonQuestions[index]
    if (question.imageData === undefined) {
      eventBus.$set(question, 'imageData', imageData)
    } else {
      question.imageData = imageData
    }
  }

  const hasChanges = computed(
    () => (type: 'comment' | 'title' | 'answer', questionIndex: number, answerId?: string) => {
      if (type === 'comment') {
        return hasLocalChanges.value(
          props.lessonQuestions[questionIndex].comment,
          props.originalQuestions[questionIndex]?.comment,
        )
      }
      if (type === 'title') {
        return hasLocalChanges.value(
          props.lessonQuestions[questionIndex].title,
          props.originalQuestions[questionIndex]?.title,
        )
      }
      const editorAnswer = getAnswer(questionIndex, answerId!)
      if (!(editorAnswer.details || editorAnswer.correct)) return false
      const lessonAnswer = props.originalQuestions[questionIndex]?.answers.find((a) => a.id === answerId)
      return hasLocalChanges.value(editorAnswer, lessonAnswer)
    },
  )
  const discardQuestionAnswer = (questionIndex: number, answerId: string) => {
    const answer = getAnswer(questionIndex, answerId)
    answer.details = ''
    answer.correct = false
  }

  const dragQuestionIndex = ref(-1)
  const dropModuleTarget = ref(null as Nullable<CourseModuleState>)
  const dropLessonId = ref('')
  const onQuestionDragStart = (questionIndex: number) => {
    dragQuestionIndex.value = questionIndex
    eventBus.$emit('question-drag-start')
  }
  const onQuestionDragEnd = () => {
    dragQuestionIndex.value = -1
    dropLessonId.value = ''
    dropModuleTarget.value = null
    eventBus.$emit('question-drag-end')
  }
  const onLessonDrop = () => {
    if (dragQuestionIndex.value === -1 || !dropLessonId.value || !dropModuleTarget.value) return

    // ignore drop on same lesson
    if (dropLessonId.value === EditorModule.currentLesson?.id) return
    const removedQuestion = props.removeLessonQuestion(dragQuestionIndex.value)
    if (removedQuestion.length) {
      const question = removedQuestion[0]
      const questionIndex = CourseModule.currentLessonQuestions!.findIndex((q) => q.id === question.id)
      if (questionIndex > -1) CourseModule.currentLessonQuestions!.splice(questionIndex, 1)

      CourseApi.updateQuestion(
        Object.assign({}, question, {
          lessonId: dropLessonId.value,
          answers: undefined,
          editorId: undefined,
        }),
      ).then(async () => {
        eventBus.$emit('turn-on-loader')
        await CourseModule.getCourseModules(currentCourseId.value)
        EditorModule.setCourseModules({
          courseId: currentCourseId.value,
          modules: structuredClone(CourseModule.courseModules(currentCourseId.value)),
        })
        EditorModule.setCurrentLessonQuestions(CourseModule.currentLessonQuestions)
        eventBus.$emit('turn-off-loader')
        eventBus.$toasted.success(translateString('editor.messages.questionMovedToLessonSuccessfully'))
      })
    }
  }

  const bindQuestionDragDropEvents = () => {
    eventBus.$on('lesson-drop', onLessonDrop)
    eventBus.$on('question-drag', (module: CourseModuleState, lessonId: string) => {
      dropModuleTarget.value = module
      dropLessonId.value = lessonId
    })
  }

  const questionRedoCallback = (data: EditorStatePayload) => {
    if (!data) return
    const question = props.lessonQuestions?.find((q) =>
      [(data.value as LessonMap).questionId].includes(q.editorId! || q.id!),
    )
    if (question) (question as any)[data.property!] = data.apiPayload as string
  }
  const questionUndoCallback = (data: EditorStatePayload) => {
    if (!data) return
    const question = props.lessonQuestions?.find((q) =>
      [(data.value as LessonMap).questionId].includes(q.editorId! || q.id!),
    )
    if (question) (question as any)[data.property!] = (data.value as LessonMap).prevPropValue
  }

  const mutateAndQueueFunc = (questionId: string) => {
    return async (data: EditorStatePayload) => {
      const changesPayload = EditorModule.editorChanges[data.path]?.find((c) => c.data.key === data.key)
      const question: any = props.lessonQuestions?.find((q) => [questionId].includes(q.editorId! || q.id!))

      const payload = Object.assign({}, data, {
        apiPayload: data.value,
        value: {
          questionId,
          prevPropValue: (changesPayload?.data.value as LessonMap)?.prevPropValue ?? question[data.property!],
        },
      })
      EditorModule.addEditorChange(payload)
      if (question) question![data.property!] = payload.apiPayload as string
      EditorModule.subscribe({
        type: 'save',
        key: data.key,
        callback: questionRedoCallback,
      })
      EditorModule.subscribe({
        type: 'discard',
        key: 'discard-' + data.key,
        callback: questionUndoCallback,
      })
    }
  }

  onMounted(() => {
    bindQuestionDragDropEvents()
  })

  onBeforeUnmount(() => {
    EditorModule.unsubscribe({ type: 'discard', callback: questionUndoCallback })
    EditorModule.unsubscribe({ type: 'save', callback: questionRedoCallback })
    props.originalQuestions.forEach((q) => {
      eventBus.$set(questionStates, q.id as string, true)
      eventBus.$set(delayOpen, q.id as string, true)
    })
  })

return { __sfc: true,props, route, translateString, checkboxSquareChecked, checkboxSquare, hasLocalChanges, currentCourseId, questionStates, delayOpen, transitionName, questionAnswers, originalQuestion, questionHeaderTitle, addNewQuestion, addNewAnswer, dropdownItems, removeQuestion, getAnswer, onQuestionTypeChange, onCorrectAnswer, removeAnswer, removeQuestionImage, onImageUpload, hasChanges, discardQuestionAnswer, dragQuestionIndex, dropModuleTarget, dropLessonId, onQuestionDragStart, onQuestionDragEnd, onLessonDrop, bindQuestionDragDropEvents, questionRedoCallback, questionUndoCallback, mutateAndQueueFunc, computed, onBeforeUnmount, onMounted, reactive, ref, KetchUpButton, TextRenderer, SVGRenderer, Dropdown, VideoOrImageRenderer, CollapsibleWidget, DurationInput, useRoute, useIcons, useEditor, useCourse, useI18n, EditorModule, CourseModule, CourseApi, uuidv4, eventBus }
}

})